import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Avatar,
  Container,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
} from '@mui/material'
import { useState, useEffect } from 'react'
import useSWR from 'swr'
import { useAuth, useCart, useThemeMode } from 'core'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { CartButton, FavouritesButton, LiveSearch, NotificationBell, CategoryMegaMenu } from './components'
import { PageURLs } from 'Routes'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded'
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import LoginRoundedIcon from '@mui/icons-material/LoginRounded'
import { motion } from 'framer-motion'

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Categories', path: '/categories' },
  { label: 'Products', path: '/products' },
  { label: 'Brands', path: '/brands' },
  { label: 'Offers', path: '/offers' },
]

const NavigationBar = () => {
  const { user, logout, isAuthenticated } = useAuth()
  const { quantity } = useCart()
  const { mode, toggleMode } = useThemeMode()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()

  const [scrolled, setScrolled] = useState(false)
  const [anchorElUser, setAnchorElUser] = useState(null)
  const [catMenuAnchor, setCatMenuAnchor] = useState(null)
  const [catMenuOpen, setCatMenuOpen] = useState(false)
  const { data: publicSettings } = useSWR('settings/public')

  const closeCatMenu = () => {
    setCatMenuOpen(false)
    setCatMenuAnchor(null)
  }

  // Close ALL menus on every route change
  useEffect(() => {
    setAnchorElUser(null)
    closeCatMenu()
  }, [location.pathname])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleOpenUserMenu = (e) => setAnchorElUser(e.currentTarget)
  const handleCloseUserMenu = () => setAnchorElUser(null)

  const handleLogout = () => {
    handleCloseUserMenu()
    logout({ sessionExpired: false })
  }

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: scrolled
          ? theme.palette.mode === 'dark'
            ? 'rgba(7, 11, 20, 0.85)'
            : 'rgba(255, 255, 255, 0.85)'
          : 'transparent',
        backdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
        borderBottom: scrolled
          ? `1px solid ${
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
            }`
          : 'none',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: theme.zIndex.appBar,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{ height: { xs: 64, md: 72 }, justifyContent: 'space-between', gap: 2 }}
        >
          {/* Logo */}
          <Box
            component={Link}
            to="/"
            onClick={closeCatMenu}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              textDecoration: 'none',
            }}
          >
            <Box
              component="img"
              src={publicSettings?.store_logo || (isMobile ? '/images/bazaar-black-sm.svg' : '/images/logo2.svg')}
              alt="Logo"
              sx={{
                height: { xs: 36, md: 44 },
                width: 'auto',
                maxWidth: { xs: 120, md: 160 },
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>

          {/* Desktop nav links */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {navLinks.map((link) => (
                link.label === 'Categories' ? (
                  <Box
                    key={link.path}
                    onClick={(e) => {
                      if (catMenuOpen) closeCatMenu()
                      else {
                        setCatMenuAnchor(e.currentTarget)
                        setCatMenuOpen(true)
                      }
                    }}
                    sx={{
                      px: 2, py: 1, borderRadius: 2, fontSize: '0.875rem', fontWeight: 600,
                      color: catMenuOpen ? theme.palette.primary.main : theme.palette.text.primary,
                      cursor: 'pointer', letterSpacing: '-0.01em',
                      background: catMenuOpen ? `${theme.palette.primary.main}10` : 'transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': { color: theme.palette.primary.main, background: `${theme.palette.primary.main}10` },
                    }}
                  >
                    {link.label}
                  </Box>
                ) : (
                  <Box
                    key={link.path}
                    component={Link}
                    to={link.path}
                    onClick={closeCatMenu}
                    sx={{
                      px: 2, py: 1, borderRadius: 2, fontSize: '0.875rem', fontWeight: 600,
                      color: theme.palette.text.primary, textDecoration: 'none', transition: 'all 0.2s ease',
                      letterSpacing: '-0.01em',
                      '&:hover': { color: theme.palette.primary.main, background: `${theme.palette.primary.main}10` },
                    }}
                  >
                    {link.label}
                  </Box>
                )
              ))}
              <CategoryMegaMenu
                key={location.pathname}
                anchorEl={catMenuAnchor}
                open={catMenuOpen && Boolean(catMenuAnchor)}
                onClose={closeCatMenu}
              />
            </Box>
          )}

          {/* Desktop search */}
          {!isMobile && (
            <Box sx={{ flex: 1, maxWidth: 400, mx: 2 }}>
              <LiveSearch />
            </Box>
          )}

          {/* Right actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
            {/* Mobile search icon */}
            <IconButton
              onClick={() => navigate(PageURLs.SearchResults)}
              size="small"
              sx={{
                display: { xs: 'flex', md: 'none' },
                color: theme.palette.text.primary,
                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                borderRadius: '50%',
                width: 40, height: 40,
              }}
            >
              <SearchRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {/* Theme toggle — always visible */}
            <Tooltip title={mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
              <IconButton
                onClick={toggleMode}
                size="small"
                sx={{
                  color: theme.palette.text.primary,
                  background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  borderRadius: '50%',
                  width: 40, height: 40,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: `${theme.palette.primary.main}18`,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {mode === 'dark'
                  ? <LightModeRoundedIcon sx={{ fontSize: 20 }} />
                  : <DarkModeRoundedIcon sx={{ fontSize: 20 }} />
                }
              </IconButton>
            </Tooltip>

            {/* Notification bell — always visible (not in bottom nav) */}
            <NotificationBell />

            {/* Wishlist — always visible */}
            <FavouritesButton />

            {/* Cart — always visible */}
            <CartButton />

            {/* User avatar / login — always visible */}
            <Tooltip title={isAuthenticated ? 'Account' : 'Sign in'}>
              <IconButton
                onClick={isAuthenticated ? handleOpenUserMenu : () => navigate(PageURLs.Login)}
                sx={{ p: 0.5 }}
              >
                <Avatar
                  alt={user?.name}
                  src={user?.avatar}
                  sx={{
                    width: 36,
                    height: 36,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      border: `2px solid ${theme.palette.primary.main}`,
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  {user?.name?.[0]?.toUpperCase() || <PersonRoundedIcon sx={{ fontSize: 18 }} />}
                </Avatar>
              </IconButton>
            </Tooltip>

            {/* User dropdown */}
            <Menu
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
              disableScrollLock
              keepMounted={false}
              PaperProps={{
                elevation: 2,
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(15, 22, 41, 0.95)'
                    : 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
                  }`,
                  overflow: 'hidden',
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {/* User info header */}
              {isAuthenticated && user && (
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} noWrap>
                    {user.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {user.email}
                  </Typography>
                </Box>
              )}
              {isAuthenticated && <Divider />}

              <MenuItem
                onClick={() => { navigate(PageURLs.Profile); handleCloseUserMenu() }}
                sx={{ py: 1.25, gap: 1.5, borderRadius: 2, mx: 1, my: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 'auto' }}>
                  <PersonRoundedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2" fontWeight={500}>My Profile</Typography>
              </MenuItem>

              <MenuItem
                onClick={() => { navigate(PageURLs.OrderHistory); handleCloseUserMenu() }}
                sx={{ py: 1.25, gap: 1.5, borderRadius: 2, mx: 1, my: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 'auto' }}>
                  <HistoryRoundedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2" fontWeight={500}>Orders</Typography>
              </MenuItem>

              <MenuItem
                onClick={() => { navigate(PageURLs.Settings); handleCloseUserMenu() }}
                sx={{ py: 1.25, gap: 1.5, borderRadius: 2, mx: 1, my: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 'auto' }}>
                  <SettingsRoundedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2" fontWeight={500}>Settings</Typography>
              </MenuItem>

              <MenuItem
                onClick={() => { navigate('/support'); handleCloseUserMenu() }}
                sx={{ py: 1.25, gap: 1.5, borderRadius: 2, mx: 1, my: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 'auto' }}>
                  <PersonRoundedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2" fontWeight={500}>Support</Typography>
              </MenuItem>

              <Divider sx={{ my: 0.5 }} />

              <MenuItem
                onClick={handleLogout}
                sx={{ py: 1.25, gap: 1.5, borderRadius: 2, mx: 1, my: 0.5, color: 'error.main' }}
              >
                <ListItemIcon sx={{ minWidth: 'auto', color: 'error.main' }}>
                  <LogoutRoundedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2" fontWeight={500}>Sign Out</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  )
}

export default NavigationBar
